import React, { useContext } from "react";
import {
  DemoKeyValueList,
  DemoWorkbenchCard,
  DemoWorkbenchPill,
  type DemoKeyValueItem,
} from "@csk/demo-ui";
import { PathSelectionContext } from "../context";
import { buildProofTreeFromReceipt } from "../proofTree";
import { guardProofTree, type ProofTree } from "../types";

function proofPinItems(
  pins: Readonly<Record<string, string>>,
): readonly DemoKeyValueItem[] {
  return Object.entries(pins).map(
    ([key, value]): DemoKeyValueItem => ({
      id: key,
      label: key,
      value,
      mono: true,
    }),
  );
}

function NodeView({
  node,
  depth,
}: {
  node: ProofTree;
  depth: number;
}): React.JSX.Element {
  return (
    <div className="mt-2" style={{ marginLeft: depth * 14 }}>
      <div className="flex items-start gap-2">
        {node.ok === null ? (
          <DemoWorkbenchPill>unknown</DemoWorkbenchPill>
        ) : (
          <DemoWorkbenchPill variant={node.ok ? "success" : "danger"}>
            {node.ok ? "pass" : "fail"}
          </DemoWorkbenchPill>
        )}
        <div>
          <div className="font-semibold text-slate-100">{node.label}</div>
          {node.detail ? (
            <div className="text-xs text-slate-400 mt-0.5">{node.detail}</div>
          ) : null}
        </div>
      </div>
      {node.children?.length ? (
        <div className="mt-1 border-l border-slate-800 pl-3">
          {node.children.map((child, i) => (
            <NodeView key={i} node={child} depth={depth + 1} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function resolveTree(
  tree: ProofTree | null | undefined,
  receipt: unknown,
): ProofTree | null {
  if (tree) return tree;
  if (receipt == null) return null;
  const fallback = guardProofTree(buildProofTreeFromReceipt(receipt));
  return fallback.ok ? fallback.value : null;
}

export function ProofTreePanel(props: {
  tree?: ProofTree | null;
  receipt?: unknown;
}): React.JSX.Element {
  const { setSelection } = useContext(PathSelectionContext);
  const tree = resolveTree(props.tree, props.receipt);
  return (
    <DemoWorkbenchCard
      title="Proof-tree explainer"
      right={<DemoWorkbenchPill>BOP predicate trace</DemoWorkbenchPill>}
    >
      {!tree ? (
        <div className="text-sm text-slate-300">
          No proof tree available — the default <code>DEMO_PROOF_TREE</code>{" "}
          from <code>proofTree.ts</code> failed schema validation and no receipt
          was supplied. See the dashboard error banner for the parse failure.
        </div>
      ) : (
        <>
          <div className="mb-3 text-xs text-slate-400">
            Tip: if a matrix composition is involved, jump to Path explorer.
            <button
              className="ml-2 rounded-full border border-slate-800 bg-slate-950/40 px-3 py-1 text-slate-200 hover:bg-slate-900"
              onClick={() => {
                setSelection({
                  start: "G2.CLASSIFY_INPUT",
                  goal: "G1.HTTP_GET",
                });
              }}
            >
              Explore path: CLASSIFY → HTTP_GET
            </button>
          </div>
          <NodeView node={tree} depth={0} />
          {tree.pins ? (
            <div className="mt-4 text-xs text-slate-400">
              <div className="font-semibold text-slate-200">Pins</div>
              <DemoKeyValueList
                compact
                tone="dark"
                labelWidth="7rem"
                style={{ marginTop: 4 }}
                items={proofPinItems(tree.pins)}
              />
            </div>
          ) : null}
        </>
      )}
    </DemoWorkbenchCard>
  );
}
